import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { ConfigurationService } from './configuration.service';
import { CustomField, CustomFieldValue } from '../models/custom-field.model';

@Injectable({
  providedIn: 'root'
})
export class CustomFieldsService {
  // Active fields per entity, cached for the session; cleared whenever a definition changes.
  private activeCache = new Map<string, Observable<CustomField[]>>();

  constructor(private http: HttpClient, private configurations: ConfigurationService) {
  }

  private get baseUrl() {
    return this.configurations.apiAddress + '/api/CustomFields';
  }

  /** All definitions (incl. inactive) for the Settings screen. */
  getAll(): Observable<CustomField[]> {
    return this.http.get<CustomField[]>(this.baseUrl + '/getAll');
  }

  save(field: CustomField): Observable<CustomField> {
    return this.http.post<CustomField>(this.baseUrl + '/save', field).pipe(tap(() => this.clearCache()));
  }

  delete(id: number) {
    return this.http.delete(this.baseUrl + '/delete/' + id).pipe(tap(() => this.clearCache()));
  }

  /** Active fields for 'hco' or 'hcp'; empty while the enableHcoHcpCustomFields setting is off. */
  getActive(entity: 'hco' | 'hcp'): Observable<CustomField[]> {
    if (!this.activeCache.has(entity)) {
      const request = this.http.get<CustomField[]>(this.baseUrl + '/active', { params: new HttpParams().set('entity', entity) })
        .pipe(shareReplay(1));
      this.activeCache.set(entity, request);
      request.subscribe(() => { }, () => this.activeCache.delete(entity));
    }
    return this.activeCache.get(entity);
  }

  /** Display value of one field in a row's values ('' if none). */
  displayOf(values: CustomFieldValue[], fieldId: number): string {
    const v = (values || []).find(x => x.fieldId === fieldId);
    return v && v.displayValue ? v.displayValue : '';
  }

  /** CSV export helpers: one column per active field, appended after the built-in columns. */
  csvHeaders(fields: CustomField[], prefix = ''): string[] {
    return (fields || []).map(f => prefix + f.name);
  }

  /** Spread into an AngularCsv row object, after the built-in keys, so it lines up with csvHeaders(). */
  csvValues(values: CustomFieldValue[], fields: CustomField[], keyPrefix = 'cf_'): { [key: string]: string } {
    const row = {};
    for (const f of fields || []) {
      const v = (values || []).find(x => x.fieldId === f.id);
      row[keyPrefix + f.id] = v && v.displayValue ? v.displayValue : '';
    }
    return row;
  }

  clearCache() {
    this.activeCache.clear();
  }
}
